import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { MessageSquare, ShoppingCart, Calendar, Mail, Zap, Users, Heart, TrendingUp, Facebook, Instagram, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import PublicFooter from '../components/PublicFooter';

const Templates = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('all');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
                <MessageSquare className="h-8 w-8 text-blue-600" />
                <span className="text-2xl font-bold text-gray-900">ChatFlow</span>
              </div>
              <LanguageSwitcher />
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/login')}>{t('login')}</Button>
              <Button onClick={() => navigate('/signup')}>{t('getStarted')}</Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            {t('chatbotTemplates')}
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            {t('templatesSubtitle')}
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          <Button
            variant={selectedCategory === 'all' ? 'default' : 'outline'}
            onClick={() => setSelectedCategory('all')}
            className="rounded-full"
          >
            {t('allTemplates')}
          </Button>
          <Button
            variant={selectedCategory === 'ecommerce' ? 'default' : 'outline'}
            onClick={() => setSelectedCategory('ecommerce')}
            className="rounded-full"
          >
            {t('ecommerce')}
          </Button>
          <Button
            variant={selectedCategory === 'marketing' ? 'default' : 'outline'}
            onClick={() => setSelectedCategory('marketing')}
            className="rounded-full"
          >
            {t('marketing')}
          </Button>
          <Button
            variant={selectedCategory === 'engagement' ? 'default' : 'outline'}
            onClick={() => setSelectedCategory('engagement')}
            className="rounded-full"
          >
            {t('engagement')}
          </Button>
          <Button
            variant={selectedCategory === 'scheduling' ? 'default' : 'outline'}
            onClick={() => setSelectedCategory('scheduling')}
            className="rounded-full"
          >
            {t('scheduling')}
          </Button>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Template 1: Welcome Message */}
          {(selectedCategory === 'all' || selectedCategory === 'engagement') && (
            <Card className="relative transition-all hover:shadow-xl cursor-pointer">
              <CardHeader>
                <div className="w-12 h-12 bg-pink-100 text-pink-600 rounded-lg flex items-center justify-center mb-4">
                  <Heart className="h-6 w-6" />
                </div>
                <CardTitle className="text-xl mb-2">{t('welcomeMessage')}</CardTitle>
                <CardDescription>{t('welcomeMessageDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2 mb-4">
                  <span className="text-sm text-gray-600">{t('platforms')}:</span>
                  <div className="flex space-x-2">
                    <Facebook className="h-5 w-5 text-gray-600" />
                    <Instagram className="h-5 w-5 text-gray-600" />
                    <MessageCircle className="h-5 w-5 text-gray-600" />
                  </div>
                </div>
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">{t('useCases')}:</p>
                  <ul className="space-y-1">
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      New subscriber onboarding
                    </li>
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Brand introduction
                    </li>
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      First impression
                    </li>
                  </ul>
                </div>
                <Button className="w-full" onClick={() => navigate('/signup')}>
                  {t('useThisTemplate')}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Template 2: Abandoned Cart Recovery */}
          {(selectedCategory === 'all' || selectedCategory === 'ecommerce') && (
            <Card className="relative transition-all hover:shadow-xl cursor-pointer">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 text-green-600 rounded-lg flex items-center justify-center mb-4">
                  <ShoppingCart className="h-6 w-6" />
                </div>
                <CardTitle className="text-xl mb-2">{t('abandonedCart')}</CardTitle>
                <CardDescription>{t('abandonedCartDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2 mb-4">
                  <span className="text-sm text-gray-600">{t('platforms')}:</span>
                  <div className="flex space-x-2">
                    <Facebook className="h-5 w-5 text-gray-600" />
                    <MessageCircle className="h-5 w-5 text-gray-600" />
                  </div>
                </div>
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">{t('useCases')}:</p>
                  <ul className="space-y-1">
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Recover abandoned carts
                    </li>
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Increase conversions
                    </li>
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Special discount offers
                    </li>
                  </ul>
                </div>
                <Button className="w-full" onClick={() => navigate('/signup')}>
                  {t('useThisTemplate')}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Template 3: Appointment Booking */}
          {(selectedCategory === 'all' || selectedCategory === 'scheduling') && (
            <Card className="relative transition-all hover:shadow-xl cursor-pointer">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-4">
                  <Calendar className="h-6 w-6" />
                </div>
                <CardTitle className="text-xl mb-2">{t('appointmentBooking')}</CardTitle>
                <CardDescription>{t('appointmentBookingDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2 mb-4">
                  <span className="text-sm text-gray-600">Platforms:</span>
                  <div className="flex space-x-2">
                    <Facebook className="h-5 w-5 text-gray-600" />
                    <Instagram className="h-5 w-5 text-gray-600" />
                    <MessageCircle className="h-5 w-5 text-gray-600" />
                  </div>
                </div>
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">{t('useCases')}:</p>
                  <ul className="space-y-1">
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Book appointments
                    </li>
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Send reminders
                    </li>
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Reduce no-shows
                    </li>
                  </ul>
                </div>
                <Button className="w-full" onClick={() => navigate('/signup')}>
                  {t('useThisTemplate')}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Template 4: Lead Generation */}
          {(selectedCategory === 'all' || selectedCategory === 'marketing') && (
            <Card className="relative transition-all hover:shadow-xl cursor-pointer">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center mb-4">
                  <Users className="h-6 w-6" />
                </div>
                <CardTitle className="text-xl mb-2">{t('leadGeneration')}</CardTitle>
                <CardDescription>{t('leadGenerationDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2 mb-4">
                  <span className="text-sm text-gray-600">Platforms:</span>
                  <div className="flex space-x-2">
                    <Facebook className="h-5 w-5 text-gray-600" />
                    <Instagram className="h-5 w-5 text-gray-600" />
                    <MessageCircle className="h-5 w-5 text-gray-600" />
                  </div>
                </div>
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">{t('useCases')}:</p>
                  <ul className="space-y-1">
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Collect contact info
                    </li>
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Qualify leads
                    </li>
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Schedule demos
                    </li>
                  </ul>
                </div>
                <Button className="w-full" onClick={() => navigate('/signup')}>
                  {t('useThisTemplate')}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Template 5: Order Status Updates */}
          {(selectedCategory === 'all' || selectedCategory === 'ecommerce') && (
            <Card className="relative transition-all hover:shadow-xl cursor-pointer">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6" />
                </div>
                <CardTitle className="text-xl mb-2">{t('orderStatus')}</CardTitle>
                <CardDescription>{t('orderStatusDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2 mb-4">
                  <span className="text-sm text-gray-600">{t('platforms')}:</span>
                  <div className="flex space-x-2">
                    <Facebook className="h-5 w-5 text-gray-600" />
                    <MessageCircle className="h-5 w-5 text-gray-600" />
                  </div>
                </div>
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">{t('useCases')}:</p>
                  <ul className="space-y-1">
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Order confirmations
                    </li>
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Shipping updates
                    </li>
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Delivery notifications
                    </li>
                  </ul>
                </div>
                <Button className="w-full" onClick={() => navigate('/signup')}>
                  {t('useThisTemplate')}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Template 6: Newsletter Subscription */}
          {(selectedCategory === 'all' || selectedCategory === 'marketing') && (
            <Card className="relative transition-all hover:shadow-xl cursor-pointer">
              <CardHeader>
                <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center mb-4">
                  <Mail className="h-6 w-6" />
                </div>
                <CardTitle className="text-xl mb-2">{t('newsletter')}</CardTitle>
                <CardDescription>{t('newsletterDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2 mb-4">
                  <span className="text-sm text-gray-600">Platforms:</span>
                  <div className="flex space-x-2">
                    <Facebook className="h-5 w-5 text-gray-600" />
                    <Instagram className="h-5 w-5 text-gray-600" />
                  </div>
                </div>
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">{t('useCases')}:</p>
                  <ul className="space-y-1">
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Build email list
                    </li>
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Send newsletters
                    </li>
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Content distribution
                    </li>
                  </ul>
                </div>
                <Button className="w-full" onClick={() => navigate('/signup')}>
                  {t('useThisTemplate')}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Template 7: Customer Feedback */}
          {(selectedCategory === 'all' || selectedCategory === 'engagement') && (
            <Card className="relative transition-all hover:shadow-xl cursor-pointer">
              <CardHeader>
                <div className="w-12 h-12 bg-yellow-100 text-yellow-600 rounded-lg flex items-center justify-center mb-4">
                  <MessageSquare className="h-6 w-6" />
                </div>
                <CardTitle className="text-xl mb-2">{t('customerFeedback')}</CardTitle>
                <CardDescription>{t('customerFeedbackDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2 mb-4">
                  <span className="text-sm text-gray-600">{t('platforms')}:</span>
                  <div className="flex space-x-2">
                    <Facebook className="h-5 w-5 text-gray-600" />
                    <MessageCircle className="h-5 w-5 text-gray-600" />
                  </div>
                </div>
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">{t('useCases')}:</p>
                  <ul className="space-y-1">
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Collect reviews
                    </li>
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Get feedback
                    </li>
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Improve service
                    </li>
                  </ul>
                </div>
                <Button className="w-full" onClick={() => navigate('/signup')}>
                  {t('useThisTemplate')}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Template 8: Flash Sale Announcement */}
          {(selectedCategory === 'all' || selectedCategory === 'ecommerce') && (
            <Card className="relative transition-all hover:shadow-xl cursor-pointer">
              <CardHeader>
                <div className="w-12 h-12 bg-red-100 text-red-600 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="h-6 w-6" />
                </div>
                <CardTitle className="text-xl mb-2">{t('flashSale')}</CardTitle>
                <CardDescription>{t('flashSaleDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2 mb-4">
                  <span className="text-sm text-gray-600">Platforms:</span>
                  <div className="flex space-x-2">
                    <Facebook className="h-5 w-5 text-gray-600" />
                    <Instagram className="h-5 w-5 text-gray-600" />
                    <MessageCircle className="h-5 w-5 text-gray-600" />
                  </div>
                </div>
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">{t('useCases')}:</p>
                  <ul className="space-y-1">
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Announce sales
                    </li>
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Create urgency
                    </li>
                    <li className="text-sm text-gray-600 flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      Drive conversions
                    </li>
                  </ul>
                </div>
                <Button className="w-full" onClick={() => navigate('/signup')}>
                  {t('useThisTemplate')}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-white rounded-2xl shadow-lg p-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {t('cantFindTemplate')}
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            {t('createCustomFlow')}
          </p>
          <Button size="lg" onClick={() => navigate('/signup')}>
            {t('startBuildingNow')}
          </Button>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default Templates;
